import { SupportedLocale, SUPPORTED_LOCALES, UI_TO_CMS_MAP } from '../../cms/cmsLocaleConstants';

export type QuotationMarks = {
    outerOpen: string;
    outerClose: string;
    innerOpen: string;
    innerClose: string;
};

const QUOTATION_MARK_PRESETS = {
    englishSmart: {
        outerOpen: '\u201C',
        outerClose: '\u201D',
        innerOpen: '\u2018',
        innerClose: '\u2019',
    },
    frenchGuillemets: {
        outerOpen: '\u00AB\u00A0',
        outerClose: '\u00A0\u00BB',
        innerOpen: '\u201C',
        innerClose: '\u201D',
    },
    angleGuillemets: {
        outerOpen: '\u00AB',
        outerClose: '\u00BB',
        innerOpen: '\u201C',
        innerClose: '\u201D',
    },
    germanLowHigh: {
        outerOpen: '\u201E',
        outerClose: '\u201C',
        innerOpen: '\u201A',
        innerClose: '\u2018',
    },
    polishLowHigh: {
        outerOpen: '\u201E',
        outerClose: '\u201D',
        innerOpen: '\u201A',
        innerClose: '\u2019',
    },
    japaneseKagi: {
        outerOpen: '\u300C',
        outerClose: '\u300D',
        innerOpen: '\u300E',
        innerClose: '\u300F',
    },
} as const;

type QuotationMarkPreset = (typeof QUOTATION_MARK_PRESETS)[keyof typeof QUOTATION_MARK_PRESETS];

const DEFAULT_QUOTATION_MARKS: QuotationMarks = QUOTATION_MARK_PRESETS.englishSmart;

const LOCALE_TO_QUOTATION_MARKS: Partial<Record<SupportedLocale, QuotationMarkPreset>> = {
    [UI_TO_CMS_MAP.es]: QUOTATION_MARK_PRESETS.angleGuillemets,
    [UI_TO_CMS_MAP.fr]: QUOTATION_MARK_PRESETS.frenchGuillemets,
    [UI_TO_CMS_MAP.de]: QUOTATION_MARK_PRESETS.germanLowHigh,
    [UI_TO_CMS_MAP.ru]: QUOTATION_MARK_PRESETS.angleGuillemets,
    [UI_TO_CMS_MAP.pl]: QUOTATION_MARK_PRESETS.polishLowHigh,
    [UI_TO_CMS_MAP.ja]: QUOTATION_MARK_PRESETS.japaneseKagi,
    [UI_TO_CMS_MAP.it]: QUOTATION_MARK_PRESETS.angleGuillemets,
};

const getNormalizedLocalePreference = (localePreference?: string): string =>
    localePreference?.trim().toLowerCase() || '';

export const getQuotationMarksForLocalePreference = (localePreference?: string): QuotationMarks => {
    const normalizedLocalePreference = getNormalizedLocalePreference(localePreference);
    if (!normalizedLocalePreference) return DEFAULT_QUOTATION_MARKS;

    const marksFromLocale = LOCALE_TO_QUOTATION_MARKS[normalizedLocalePreference as SupportedLocale];
    return marksFromLocale || DEFAULT_QUOTATION_MARKS;
};

export const wrapWithLocaleDoubleQuotes = (text: string, localePreference?: string): string => {
    const { outerOpen, outerClose } = getQuotationMarksForLocalePreference(localePreference);
    return `${outerOpen}${text}${outerClose}`;
};

export const getSupportedQuotationMarksByLocale = (): Record<SupportedLocale, QuotationMarks> => {
    const marksByLocale = {} as Record<SupportedLocale, QuotationMarks>;
    SUPPORTED_LOCALES.forEach((locale) => {
        marksByLocale[locale] = getQuotationMarksForLocalePreference(locale);
    });
    return marksByLocale;
};
